import { useState } from 'react';
import { CheckCircle, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import type { PlanData } from '@/hooks/usePlanSynthesis';

interface PlanAssemblyLevelSetProps {
  planData: PlanData | null;
  isLoading: boolean;
  onComplete: () => void;
  onClose: () => void;
}

type ScreenState = 'readiness' | 'complete';

function getBarColor(score: number): string {
  if (score >= 70) return 'bg-teal-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function formatPercent(score: number | null): string {
  if (score == null) return '—';
  return `${Math.round(score)}%`;
}

export function PlanAssemblyLevelSet({ planData, isLoading, onComplete, onClose }: PlanAssemblyLevelSetProps) {
  const [screenState, setScreenState] = useState<ScreenState>('readiness');
  const [generating, setGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!planData) return;
    setGenerating(true);
    setErrorMsg(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: upsertErr } = await supabase.rpc('create_or_update_succession_plan', {
        p_user_id: user.id,
        p_plan_data: JSON.parse(JSON.stringify(planData)),
      });
      if (upsertErr) throw upsertErr;

      const { error: confirmErr } = await supabase.rpc('confirm_succession_plan', {
        p_user_id: user.id,
      });
      if (confirmErr) throw confirmErr;

      setScreenState('complete');
    } catch (e) {
      logger.error('PlanAssemblyLevelSet: generation failed', e);
      setErrorMsg('Something went wrong. Please try again.');
      setGenerating(false);
    }
  };

  // ── Complete state ──
  if (screenState === 'complete') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
        <div className="max-w-lg w-full text-center space-y-6">
          <CheckCircle2 className="h-16 w-16 text-teal-500 mx-auto" />
          <h2 className="text-2xl font-semibold text-foreground">
            Your mySuccession Plan has been built.
          </h2>
          <p className="text-muted-foreground text-[15px] leading-relaxed">
            It reflects your current assessment data and will update as your situation evolves.
          </p>
          <Button
            onClick={onComplete}
            className="bg-primary hover:bg-primary/90"
          >
            View My Plan →
          </Button>
        </div>
      </div>
    );
  }

  // ── Readiness state (existing Level-Set content) ──
  return (
    <div className="flex-1 flex flex-col overflow-y-auto px-8 py-4">
      <div className="max-w-xl mx-auto w-full space-y-4 flex-1 flex flex-col">
      {/* Headline */}
        <h2 className="text-2xl font-semibold text-foreground">Your Succession Readiness</h2>

        {/* Section 1 — Score */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Your mySuccession Score
          </p>

          {isLoading ? (
            <>
              <Skeleton className="h-10 w-24" />
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="space-y-1">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-1.5 w-full rounded-full" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <p className="text-4xl font-bold text-foreground">
                  {formatPercent(planData?.overall_score ?? null)}
                </p>
                {planData?.overall_score != null && (
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                    planData.overall_score >= 70
                      ? 'bg-teal-100 text-teal-700'
                      : planData.overall_score >= 50
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {planData.overall_score >= 70
                      ? 'On Track'
                      : planData.overall_score >= 50
                        ? 'Moderate Readiness'
                        : 'Needs Attention'}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {(planData?.pillars ?? []).map((pillar) => (
                  <div key={pillar.pillar_name} className="space-y-0.5">
                    <p className="text-sm font-medium text-foreground">{pillar.pillar_name}</p>
                    <div className="h-1.5 w-full rounded-full bg-muted">
                      <div
                        className={`h-1.5 rounded-full transition-all ${getBarColor(pillar.score)}`}
                        style={{ width: `${Math.round(pillar.score)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Section 2 — Strengths */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Where You're Strong
          </p>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-56" />
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              {(planData?.strengths ?? []).map((s) => (
                <div key={s.pillar_name} className="flex items-center gap-2.5">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0" />
                  <span className="text-[15px] text-foreground">{s.pillar_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3 — Gaps */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Areas to Explore Further
          </p>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-64" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {(planData?.gaps ?? []).map((g) => (
                <div key={g.section_name} className="flex items-start gap-2.5">
                  <ArrowRight className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[15px] text-foreground">{g.section_name}</p>
                    <p className="text-xs text-muted-foreground">{g.pillar_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error message */}
        {errorMsg && (
          <p className="text-sm text-destructive">{errorMsg}</p>
        )}

        {/* CTA — pinned to bottom */}
        <div className="mt-auto pt-4">
          <Button
            onClick={handleGenerate}
            disabled={generating || isLoading}
            className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Compiling your plan...
              </>
            ) : (
              'Generate My Plan →'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
